// ThemeContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  useFetchPersonalSettingsQuery,
  useUpdateDarkModeMutation,
} from "./slice/logdetailSlice";
import { toast } from "react-toastify";

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const { data: personal_details, isSuccess } = useFetchPersonalSettingsQuery();
  const [updateDarkMode] = useUpdateDarkModeMutation();

  // Step 1: Load from localStorage immediately
  const initialMode = localStorage.getItem("theme") === "dark";
  const [isDarkMode, setIsDarkMode] = useState(initialMode);

  // Step 2: On mount, apply theme from localStorage immediately
  useEffect(() => {
    applyTheme(initialMode);
  }, []); // Only on initial mount

  // Step 3: When API responds, overwrite local if needed
  useEffect(() => {
    if (isSuccess && personal_details?.theme) {
      const apiMode = personal_details.theme.toLowerCase();
      const isDark = apiMode === "dark";

      // Update only if it differs
      if (isDark !== isDarkMode) {
        setIsDarkMode(isDark);
        localStorage.setItem("theme", isDark ? "dark" : "light");
        applyTheme(isDark);
      }
    }
  }, [isSuccess, personal_details]);

  // Step 4: Apply theme to document.body
  const applyTheme = (dark) => {
    if (dark) {
      document.body.classList.add("bg-dark", "text-white");
      document.body.classList.remove("bg-white", "text-dark");
    } else {
      document.body.classList.add("bg-white", "text-dark");
      document.body.classList.remove("bg-dark", "text-white");
    }
  };

  // Step 5: Toggle theme
  const toggleTheme = async () => {
    const newMode = !isDarkMode;

    // 1. Instantly update UI and localStorage (optimistic update)
    setIsDarkMode(newMode);
    localStorage.setItem("theme", newMode ? "dark" : "light");
    applyTheme(newMode);

    // 2. Perform backend mutation asynchronously in the background
    try {
      await updateDarkMode(newMode).unwrap();
    } catch (error) {
      // Revert to previous state if backend update fails
      setIsDarkMode(!newMode);
      localStorage.setItem("theme", !newMode ? "dark" : "light");
      applyTheme(!newMode);
      toast.error("❌ Failed to update theme on server.");
    }
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
